@if (agent) {
    <!-- Clicking the card opens the agent; its own controls stop short of that in cardClicked() -->
    <div
        class="agpr-agentCard agpr-contextMenu-parent"
        (click)="cardClicked($event)"
        [ngClass]="expanded ? 'agpr-agentCard--expanded' : 'agpr-agentCard--collapsed'"
        [@containerState]="{
            value: expanded ? 'expanded' : 'collapsed',
            params: { cardHeight: cardHeight }
        }"
        role="group"
        [attr.aria-label]="agent.name"
        tabindex="0"
    >
        @if (!expanded) {
            <div class="agpr-agentCard-summary">
                <div class="agpr-agentCard-summaryLeft">
                    <span class="agpr-agentCard-name">{{ agent.name }}</span>
                    @if (agent.hasSourceErrors) {
                        <span
                            role="img"
                            class="fa-solid fa-circle-exclamation agpr-agentCard-errorIcon"
                            agprTooltip="AutomationAgents.card.sourceErrors"
                            tooltipPosition="top"
                            [attr.aria-label]="'AutomationAgents.card.sourceErrors' | localize"
                        ></span>
                    }
                </div>


                <div class="agpr-agentCard-summaryRight">
                    <agpr-button
                        [id]="'agentCard_' + agent.id + '_toggle'"
                        class="agpr-agentCard-summaryToggle"
                        [ariaLabel]="'Core.expand'"
                        [tooltip]="'Core.expand'"
                        [style]="buttonTypes.link"
                        (click)="toggleCard()"
                    >
                        @if (activeSourceCount > 0) {
                            <span class="fa-solid fa-signal-stream agpr-agentCard-instancesIcon" aria-hidden="true"></span>
                        }
                        <span class="agpr-agentCard-instances">
                            {{ 'AutomationAgents.card.activeInstances' | localize: { active: activeSourceCount, total: totalSourceCount } }}
                        </span>
                        <span class="fa-solid fa-angle-down agpr-agentCard-summaryToggleIcon"></span>
                    </agpr-button>
                </div>
            </div>
        } @else {
            <div class="agpr-agentCard-details">
                <div class="agpr-agentCard-detailsHeader">
                    <div class="agpr-agentCard-name" [agprTooltip]="agent.name" tooltipPosition="top">
                        {{ agent.name }}
                    </div>
                    <div class="agpr-agentCard-detailsMenu">
                        <agpr-button
                            [id]="'agentCard_' + agent.id + '_edit'"
                            [style]="buttonTypes.icon"
                            icon="fa-regular fa-duotone fa-pen"
                            tooltip="AutomationAgents.card.edit"
                            tooltipPosition="top"
                            (click)="edited.emit(agent)"
                        ></agpr-button>
                        <agpr-button
                            [id]="'agentCard_' + agent.id + '_delete'"
                            [style]="buttonTypes.icon"
                            icon="fa-regular fa-duotone fa-trash-can"
                            tooltip="AutomationAgents.card.delete"
                            tooltipPosition="top"
                            (click)="deleted.emit(agent)"
                        ></agpr-button>
                    </div>
                </div>
                <div class="agpr-agentCard-detailsScroll">
                    <!-- The prompt the agent judges mentions against, and the way to its manager -->
                    <div class="agpr-agentCard-prompt">
                        <agpr-button
                            [id]="'agentCard_' + agent.id + '_openPrompt'"
                            class="agpr-agentCard-promptLink"
                            [style]="buttonTypes.link"
                            [tooltip]="'Core.navigation.contentManagement.openInPanelTooltip'"
                            (click)="openPromptManager()"
                        >
                            <span class="agpr-agentCard-promptLink">
                                <span class="agpr-agentCard-promptLinkName">{{ promptName }}</span>
                                <span class="fa-solid fa-window-restore agpr-agentCard-promptLinkIcon"></span>
                            </span>

                        </agpr-button>
                    </div>

                    <div class="agpr-agentCard-actions">
                        <!-- Grouped the way the panel edits them: what runs on a match, and on a miss -->
                        @for (outcome of outcomeList; track outcome) {
                            @if (actionsFor(outcome).length) {
                                <div class="agpr-agentCard-actionsGroup">
                                    <h5 class="agpr-agentCard-actionGroupHeading">{{ outcomeLabel(outcome) | localize }}</h5>

                                    <ul class="agpr-agentCard-actionChips">
                                        @for (action of actionsFor(outcome); track $index) {
                                            <li class="agpr-agentCard-actionChip">
                                                <agpr-action-chip
                                                    [id]="'agentCard_' + agent.id + '_action_' + $index"
                                                    [action]="action"
                                                    (viewActionTarget)="viewActionTarget(action, $event)"
                                                ></agpr-action-chip>
                                            </li>
                                        }
                                    </ul>
                                </div>
                            }
                        }
                    </div>
                </div>
            </div>

            <agpr-button
                [id]="'agentCard_' + agent.id + '_toggle'"
                class="agpr-agentCard-expandedToggle agpr-expandToggle"
                [style]="buttonTypes.icon"
                [icon]="'fa-solid fa-angle-up'"
                [tooltip]="'Core.collapse'"
                (click)="toggleCard()"
            ></agpr-button>

            <div class="agpr-agentCard-sources">
                <div class="agpr-agentCard-sourcesHeader">
                    <span>{{ 'AutomationAgents.card.column.status' | localize }}</span>
                    <span>{{ 'AutomationAgents.card.column.source' | localize }}</span>
                    <span class="agpr-agentCard-sourcesHeader--count">
                        <span>{{ 'AutomationAgents.card.column.analyzed' | localize }}</span>
                        <span>{{ 'AutomationAgents.card.column.window' | localize }}</span>
                    </span>
                    <span class="agpr-agentCard-sourcesHeader--count">
                        <span>{{ 'AutomationAgents.card.column.curated' | localize }}</span>
                        <span>{{ 'AutomationAgents.card.column.window' | localize }}</span>
                    </span>
                    <span></span>
                </div>

                <ul class="agpr-agentCard-sourceList">
                    @for (source of completeSources; track source.id ?? $index; let rowIndex = $index) {
                            <li class="agpr-agentCard-sourceRow">
                                <agpr-social-switch
                                    [id]="'agentCard_' + agent.id + '_source_' + rowIndex + '_status'"
                                    class="agpr-agentCard-sourceStatus"
                                    [ngClass]="{
                                        'agpr-agentCard-sourceStatus--active': source.isActive,
                                        'agpr-agentCard-sourceStatus--error': source.hasError,
                                        'agpr-agentCard-sourceStatus--standby': source.isStandby
                                    }"
                                    onText="AutomationAgents.card.source.active"
                                    [tooltip]="source.isStandby ? 'AutomationAgents.card.source.standby.tooltip' : null"
                                    [offText]="`AutomationAgents.card.source.${source.status}`"
                                    [initiallyFocusable]="false"
                                    [ngModel]="source.isActive || source.isStandby"
                                    (ngModelChange)="statusChanged(source, $event)"
                                ></agpr-social-switch>

                                <span class="agpr-agentCard-sourceName">
                                    <span class="agpr-agentCard-sourceIcon" [ngClass]="sourceTypeIcons[source.dataSource?.type]" [agprTooltip]="sourceTypeLabels[source.dataSource?.type] ?? ''"></span>
                                    @if (source.dataSource?.type === sourceType.socialTopic) {
                                        {{ sourceName(source) }}
                                    } @else {
                                        <a
                                            [id]="'agentCard_' + agent.id + '_source_' + rowIndex + '_view'"
                                            class="agpr-agentCard-sourceLink"
                                            (click)="sourceViewed.emit(source)"
                                            tabindex="0"
                                            [agprTooltip]="'View ' + sourceName(source)"
                                        >{{ sourceName(source) }}</a>
                                    }
                                </span>

                                <span class="agpr-agentCard-sourceCount agpr-agentCard-sourceCount--indicated">
                                    <span
                                        role="img"
                                        class="agpr-agentCard-quotaUsage"
                                        [ngClass]="quotaUsageIcon(source)"
                                        [agprTooltip]="quotaUsageTooltip(source) | localize"
                                        tooltipPosition="top"
                                    ></span>
                                    <span class="agpr-agentCard-sourceValue">{{ source.analyzedLast24h }}</span>
                                </span>

                                <span class="agpr-agentCard-sourceCount">{{ source.curatedLast24h }}</span>

                                <!-- The kebab, not the split button: its panel is appended to the
                                        body, so it is not clipped by the card or the scroll panel -->
                                <agpr-kebab-menu
                                    class="agpr-agentCard-sourceMenu"
                                    [id]="'agentCard_' + agent.id + '_source_' + rowIndex + '_actions'"
                                    [style]="buttonTypes.icon"
                                    [options]="sourceOptions(source)"
                                    [data]="source"
                                    [ariaLabel]="'AutomationAgents.card.source.actions' | localize"
                                    panelPositionX="right"
                                ></agpr-kebab-menu>
                            </li>
                    }
                </ul>
            </div>
            <div class="agpr-contextMenu bottom right">
                <agpr-button
                    [id]="'agentCard_' + agent.id + '_view'"
                    [style]="buttonTypes.contextMenu"
                    icon="fa-regular fa-duotone fa-eye"
                    tooltip="AutomationAgents.card.view"
                    tooltipPosition="top"
                    (click)="viewed.emit(agent)"
                ></agpr-button>
            </div>
        }
    </div>
}
